// One sign-out for the whole console (backlog row 192, Storm 2026-10-21
// 17:49 "go signout fix code").
//
// BEFORE: "Sign out of console" (the old label) ended the console (Cognito) session only.
// Amazon Connect stayed signed in, so on a shared computer the next person
// skipped step 2 and worked as the previous agent's Connect phone.
//
// NOW, in this order:
//   1. Refused while the agent has any contact (ringing, live, on hold or in
//      wrap-up): signing out then would drop a caller or lose unsaved notes.
//   2. Agent set to Offline, so Connect stops routing to them (waits up to 3 s).
//   3. The Connect session is ended at <instance>/connect/logout (AWS's
//      documented logout for an embedded CCP; the response is opaque, so it
//      is fire-and-wait, up to 3 s), then Streams is shut down
//      (connect.core.terminate) so the hidden CCP lets go.
//   4. The console session is ended (auth/cognito.js logout), as before.
// Steps 2 and 3 never block step 4: a failure in either still signs the
// console out. The next sign-in therefore always shows step 2 (Connect).

const CCP_URL = import.meta.env?.VITE_CCP_URL || 'https://knktr-demo.my.connect.aws/ccp-v2';
export const SIGN_OUT_BLOCKED = 'Finish your call and wrap-up before signing out.';

const wait = ms => new Promise(r => setTimeout(r, ms));

export function connectLogoutUrl(ccpUrl = CCP_URL) {
  try { return `${new URL(ccpUrl).origin}/connect/logout`; } catch { return null; }
}

function currentAgent(connectApi) {
  try {
    if (connectApi?.agent?.initialized && typeof connectApi.Agent === 'function') return new connectApi.Agent();
  } catch { /* not signed in to Connect */ }
  return null;
}

// Returns 'blocked' (nothing done) or 'signed-out' (console logout called).
export async function signOutEverywhere({
  cognitoLogout,
  connectApi = typeof window !== 'undefined' ? window.connect : undefined,
  ccpUrl = CCP_URL,
  fetchImpl = (...a) => fetch(...a),
  timeoutMs = 3000,
  onBlocked = () => {},
} = {}) {
  const agent = currentAgent(connectApi);

  // 1. Any contact at all (Streams keeps a contact in the list through
  //    after-call work until it is cleared) means: not now.
  let contacts = [];
  try { contacts = agent?.getContacts?.() || []; } catch { contacts = []; }
  if (contacts.length > 0) { onBlocked(SIGN_OUT_BLOCKED); return 'blocked'; }

  // 2. Offline.
  if (agent) {
    try {
      const offline = (agent.getAgentStates?.() || []).find(s => String(s?.type).toLowerCase() === 'offline');
      if (offline) {
        await Promise.race([
          new Promise(res => agent.setState(offline, { success: res, failure: res })),
          wait(timeoutMs),
        ]);
      }
    } catch { /* carry on */ }
  }

  // 3. End the Connect session, then shut Streams down.
  const url = connectLogoutUrl(ccpUrl);
  if (url) {
    try {
      await Promise.race([
        fetchImpl(url, { credentials: 'include', mode: 'no-cors', cache: 'no-store' }).catch(() => {}),
        wait(timeoutMs),
      ]);
    } catch { /* carry on */ }
  }
  try { connectApi?.core?.terminate?.(); } catch { /* carry on */ }

  // 4. Console session.
  cognitoLogout();
  return 'signed-out';
}
